import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import takeFirst from './take-first';

const defaultTakePolicy = 'every';

const getTakeFunction = policy => {
  switch (policy) {
    case 'latest':
      return takeLatest;
    case 'every':
      return takeEvery;
    case 'first':
      return takeFirst;
    default:
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`unknown policy: ${policy}`);
      }
      return takeEvery;
  }
};

const convertIsWorking = selector => globalState =>
  selector(globalState).isWorking;

const convertResult = selector => globalState => selector(globalState).result;

const convertError = selector => globalState => selector(globalState).error;

/* createWorker<ParamType, ResultType, ExtraPropsType>(
 *   name: string,
 *   delegates: WorkerDelegates<ParamType, ResultType, ExtraPropsType>,
 *   extraProps?: ExtraPropsType,
 * ): Worker<ParamType, ResultType, ExtraPropsType>
 *
 * REQUEST -> shouldWork? -[YES]-> WORK -> execute() -> SUCCESS
 *                |                           |        (result, cached: false)
 *               [NO]                         v
 *                |                         ERROR
 *                v
 *             SUCCESS (result, cached: true)
 *
 * This flow allows `shouldWork()` to implement "time-to-live" and
 * avoid fetching if result is still valid, which would produce
 * `SUCCESS` with the cached result.
 *
 * The following delegate controls flow:
 *  - shouldWork(
 *      localState: WorkerState<ResultType, ExtraPropsType>,
 *      globalState: GlobalStateType,
 *      param: ParamType,
 *    ) => boolean
 *    Returns false if work should be skipped and the `localState.result`
 *    should be used instead. Returns true to dispatch `WORK` action.
 *
 * The actual work is done by the delegate:
 *  - execute(params: ParamType) => Saga<ResultType>
 *    Executes the redux-saga effect.
 *
 * Since shouldWork and cached results logic relies on local state,
 * the following delegate is used:
 *  - localStateSelector: string |
 *    (globalState: GlobalStateType) => WorkState<ResultType>
 *    If a string, then globalState[localStateSelector] is used,
 *    otherwise a selector function that operates on the global state.
 *    If the selector returns null/undefined, then the initState is used.
 *
 * Returns the created elements:
 *  - Actions: redux actions.
 *  - Generators: generator functions used to implement the Saga.
 *  - ReducerHandlers: handles redux actions, maps action string to state
 *    handler.
 *  - Saga: the redux-saga using takeLatest() or takeEvery() depending
 *    on the given delegates.takePolicy
 *  - Types: action type constants (strings).
 *  - initState: the initial state for this worker.
 *  - localStateSelector: a selector function that operates on the global
 *    state and returns a local state (the initState if it was unset).
 */
const createWorker = (name, delegates, extraProps) => {
  let worker = undefined;

  const Types = {
    ERROR: `${name}/ERROR`,
    REQUEST: `${name}/REQUEST`,
    RESET: `${name}/RESET`,
    SUCCESS: `${name}/SUCCESS`,
    WORK: `${name}/WORK`,
  };

  const Actions = {
    error: (error, params, retryCount) => ({
      error,
      params,
      retryCount,
      type: Types.ERROR,
      worker,
    }),

    request: params => ({
      params,
      type: Types.REQUEST,
      worker,
    }),

    reset: () => ({
      type: Types.RESET,
      worker,
    }),

    retryWorkOfError: error => {
      const { params, retryCount } = error;

      if (
        process.env.NODE_ENV === 'development' &&
        error.type !== Types.ERROR
      ) {
        // eslint-disable-next-line no-console
        console.error(`Expected ${Types.ERROR}, got ${error.type}`);
      }

      return {
        params,
        retryCount: retryCount + 1,
        type: Types.WORK,
        worker,
      };
    },

    success: (result, cached, params, retryCount) => ({
      cached,
      params,
      result,
      retryCount,
      type: Types.SUCCESS,
      worker,
    }),

    work: (params, retryCount) => ({
      params,
      retryCount,
      type: Types.WORK,
      worker,
    }),
  };

  const initState = {
    ...extraProps,
    error: undefined,
    isWorking: false,
    lastWorkTimestamp: undefined,
    result: undefined,
  };

  const ReducerHandlers = {
    [Types.ERROR]: (state, action) => ({
      ...state,
      error: action.error,
      isWorking: false,
    }),

    [Types.REQUEST]: state => state,

    [Types.RESET]: state => ({
      ...state,
      ...initState,
    }),

    [Types.SUCCESS]: (state, action) => ({
      ...state,
      isWorking: false,
      result: action.result,
    }),

    [Types.WORK]: state => ({
      ...state, // allows user extensions to be kept
      error: undefined,
      isWorking: true,
      lastWorkTimestamp: new Date(),
    }),
  };

  const getLocalState =
    typeof delegates.localStateSelector === 'string'
      ? globalState => globalState[delegates.localStateSelector]
      : delegates.localStateSelector;

  const localStateSelector = globalState =>
    getLocalState(globalState) || initState;

  const isWorkingSelector = convertIsWorking(localStateSelector);
  const resultSelector = convertResult(localStateSelector);
  const errorSelector = convertError(localStateSelector);

  const Selectors = {
    localState: localStateSelector,
    error: errorSelector,
    isWorking: isWorkingSelector,
    result: resultSelector,
  };

  const genRequest = function*(action) {
    const { params } = action;

    const { shouldWork } = delegates;
    if (!shouldWork) {
      yield put(Actions.work(params));
      return;
    }

    const globalState = yield select(state => state);

    const localState = localStateSelector(globalState);
    if (shouldWork(localState, globalState, params)) {
      yield put(Actions.work(params, 0));
    } else if (localState.result !== undefined && localState.result !== null) {
      yield put(Actions.success(localState.result, true, params, 0));
    } else if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        'shouldWork() returned true without actual results:',
        shouldWork
      );
    }
  };

  const genWork = function*(action) {
    const { params, retryCount } = action;
    try {
      const result = yield call(delegates.execute, params, localStateSelector);
      yield put(Actions.success(result, false, params, retryCount));
      return { result };
    } catch (error) {
      yield put(Actions.error(error, params, retryCount));
      return { error };
    }
  };

  const Generators = {
    request: genRequest,
    work: genWork,
  };

  const Saga = function*() {
    const takeFunction = getTakeFunction(
      delegates.takePolicy || defaultTakePolicy
    );
    yield takeFunction(Types.REQUEST, Generators.request);
    yield takeFunction(Types.WORK, Generators.work);
  };

  // NOTE: this is assigned so the returned worker is available
  // to functions such as Actions.work()
  // eslint-disable-next-line prefer-const
  worker = {
    Actions,
    Generators,
    ReducerHandlers,
    Saga,
    Types,
    initState,
    localStateSelector,
    Selectors,
  };
  return worker;
};

export default createWorker;



// WEBPACK FOOTER //
// ./src/redux/utils/create-worker.js